/**
 * Customer Support Dashboard — V3 Tabbed
 * Tabs: Emails (3-column inbox) | Email Funnels (Kanban)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import {
  HeadsetMic as HeadsetMicIcon,
  Phone as PhoneIcon,
  ContentCopy as CopyIcon,
  Edit as ComposeIcon,
  AccountTree as FunnelTabIcon,
  Email as EmailTabIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/api';
import ChannelSidebar from './ChannelSidebar';
import MessageList from './MessageList';
import MessageDetail from './MessageDetail';
import AccountManagerDrawer from './AccountManagerDrawer';
import ComposeDialog from './ComposeDialog';
import FunnelBoard from './FunnelBoard';
import {
  SupportInteraction,
  InboxData,
  Stats,
  EmailAccountStatus,
  PhoneNumber,
  FilterType,
  FolderType,
  TabType,
  SupportDraft,
} from './types';

const DEEP_TEAL = '#1A5F7A';
const WARM_GOLD = '#D4AC0D';

const CustomerSupport: React.FC = () => {
  // Data state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inbox, setInbox] = useState<InboxData | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [accounts, setAccounts] = useState<EmailAccountStatus[]>([]);
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([]);
  const [drafts, setDrafts] = useState<SupportDraft[]>([]);

  // UI state
  const [tab, setTab] = useState<TabType>('emails');
  const [folder, setFolder] = useState<FolderType>('inbox');
  const [filter, setFilter] = useState<FilterType>('all');
  const [search, setSearch] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<SupportInteraction | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [editDraft, setEditDraft] = useState<SupportDraft | null>(null);

  // Callback dialog
  const [callbackDialogOpen, setCallbackDialogOpen] = useState(false);
  const [callbackResult, setCallbackResult] = useState<string | null>(null);

  const fetchDrafts = useCallback(async () => {
    try {
      const data = await apiService.getSupportDrafts();
      setDrafts(data as SupportDraft[]);
    } catch {
      // Non-critical
    }
  }, []);

  const fetchInbox = useCallback(async () => {
    if (folder === 'drafts') {
      await fetchDrafts();
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const result = await apiService.getCustomerSupportInbox(filter, search, selectedAccountId || undefined, folder);
      setInbox(result.data as unknown as InboxData);
    } catch (err: any) {
      setError(err.message || 'Failed to load inbox');
    } finally {
      setLoading(false);
    }
  }, [filter, search, selectedAccountId, folder, fetchDrafts]);

  const fetchStats = useCallback(async () => {
    try {
      const result = await apiService.getCustomerSupportStats();
      const data = result.data as unknown as Stats;
      setStats(data);
      setAccounts(data.emailAccounts || []);
    } catch {
      // Non-critical
    }
  }, []);

  const fetchPhoneNumbers = useCallback(async () => {
    try {
      const result = await apiService.getPhoneNumbers();
      setPhoneNumbers(((result as any).data || []) as PhoneNumber[]);
    } catch {
      // Non-critical
    }
  }, []);

  // Initial load + auto-refresh every 2 minutes
  useEffect(() => {
    fetchInbox();
    fetchStats();
    fetchPhoneNumbers();
    fetchDrafts();
    const interval = setInterval(() => {
      fetchInbox();
      fetchStats();
    }, 120000);
    return () => clearInterval(interval);
  }, [fetchInbox, fetchStats, fetchPhoneNumbers, fetchDrafts]);

  // Mark as read when selecting an item
  const handleSelectItem = async (item: SupportInteraction) => {
    setSelectedItem(item);
    if (!item.is_read) {
      try {
        await apiService.markSupportRead(item.external_id);
        setInbox(prev => {
          if (!prev) return prev;
          const updateList = (list: SupportInteraction[]) =>
            list.map(i => i.external_id === item.external_id ? { ...i, is_read: true } : i);
          return {
            ...prev,
            voicemails: updateList(prev.voicemails),
            emails: updateList(prev.emails),
          };
        });
        setSelectedItem(prev => prev ? { ...prev, is_read: true } : prev);
        fetchStats();
      } catch {
        // Non-critical
      }
    }
  };

  // Reply handler
  const handleReply = async (body: string, accountId?: string) => {
    if (!selectedItem) return;
    try {
      await apiService.sendSupportReply({
        externalId: selectedItem.external_id,
        to: selectedItem.from_address,
        subject: selectedItem.subject || 'LifeScribe Support',
        body,
        threadId: selectedItem.gmail_thread_id || undefined,
        accountId,
      });
      fetchInbox();
    } catch (err: any) {
      setError(err.message || 'Failed to send reply');
      throw err;
    }
  };

  const handleResolve = async (notes: string, tags: string[]) => {
    if (!selectedItem) return;
    try {
      await apiService.resolveSupportItem(selectedItem.external_id, { notes, tags });
      setSelectedItem(prev => prev ? { ...prev, is_resolved: true, resolution_notes: notes } : prev);
      fetchInbox();
      fetchStats();
    } catch (err: any) {
      setError(err.message || 'Failed to resolve');
      throw err;
    }
  };

  const handleUnresolve = async () => {
    if (!selectedItem) return;
    try {
      await apiService.unresolveSupportItem(selectedItem.external_id);
      setSelectedItem(prev => prev ? { ...prev, is_resolved: false } : prev);
      fetchInbox();
      fetchStats();
    } catch (err: any) {
      setError(err.message || 'Failed to reopen');
      throw err;
    }
  };

  const handleCallback = async () => {
    if (!selectedItem?.caller_phone) return;
    try {
      const result = await apiService.initiateSupportCallback({
        phoneNumber: selectedItem.caller_phone,
        externalId: selectedItem.external_id,
      });
      const data = (result as any).data;
      setCallbackResult(data?.message || 'Callback recorded');
      setCallbackDialogOpen(true);
      fetchInbox();
    } catch (err: any) {
      setError(err.message || 'Failed to initiate callback');
    }
  };

  const handleTagsUpdated = (externalId: string, tags: string[]) => {
    setInbox(prev => {
      if (!prev) return prev;
      const updateList = (list: SupportInteraction[]) =>
        list.map(i => i.external_id === externalId ? { ...i, tags } : i);
      return { ...prev, voicemails: updateList(prev.voicemails), emails: updateList(prev.emails) };
    });
    setSelectedItem(prev => prev && prev.external_id === externalId ? { ...prev, tags } : prev);
  };

  const handleRefresh = () => {
    fetchInbox();
    fetchStats();
    fetchPhoneNumbers();
    fetchDrafts();
  };

  const handleAccountsChanged = () => {
    fetchStats();
    fetchPhoneNumbers();
    fetchInbox();
  };

  const handleFolderChange = (newFolder: FolderType) => {
    setFolder(newFolder);
    setSelectedItem(null);
  };

  // In Drafts folder, display drafts as pseudo-interactions; otherwise show normal inbox
  const allItems: SupportInteraction[] = (() => {
    if (folder === 'drafts') {
      return drafts.map(d => ({
        id: d.id,
        external_id: d.id,
        type: 'email' as const,
        direction: 'outbound' as const,
        from_address: d.account_email || '',
        to_address: d.to_address,
        subject: d.subject || '(No subject)',
        body_text: d.body_text || '',
        body_html: d.body_html || '',
        voicemail_transcript: null,
        voicemail_audio_url: null,
        caller_phone: null,
        member_user_id: null,
        member_email: null,
        member_name: null,
        gmail_thread_id: null,
        is_read: true,
        is_resolved: false,
        resolved_by: null,
        resolved_at: null,
        resolution_notes: null,
        tags: [],
        callback_initiated: false,
        callback_at: null,
        callback_status: null,
        received_at: d.updated_at,
        created_at: d.created_at,
        updated_at: d.updated_at,
        email_account_id: d.email_account_id,
      }));
    }
    if (!inbox) return [];
    return [...inbox.voicemails, ...inbox.emails].sort(
      (a, b) => new Date(b.received_at).getTime() - new Date(a.received_at).getTime()
    );
  })();

  const isEmailConfigured = accounts.length > 0;

  return (
    <Box sx={{ p: 3, height: 'calc(100vh - 128px)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <HeadsetMicIcon sx={{ fontSize: 32, color: '#8B5CF6' }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: '"Montserrat", sans-serif' }}>
              Customer Support
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Multi-channel inbox for voicemails and emails
            </Typography>
          </Box>
          {stats && (
            <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
              <Chip
                label={`${stats.unreadCount} unread`}
                size="small"
                color={stats.unreadCount > 0 ? 'error' : 'default'}
                variant="outlined"
              />
              <Chip
                label={`${stats.openCount} open`}
                size="small"
                color={stats.openCount > 0 ? 'warning' : 'success'}
                variant="outlined"
              />
            </Box>
          )}
        </Box>
        {/* New Email button — always visible */}
        <Button
          variant="contained"
          startIcon={<ComposeIcon />}
          onClick={() => { setEditDraft(null); setComposeOpen(true); }}
          sx={{ bgcolor: DEEP_TEAL, '&:hover': { bgcolor: '#154e63' }, textTransform: 'none', fontWeight: 700 }}
        >
          New Email
        </Button>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 0 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            minHeight: 40,
            '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 600 },
            '& .Mui-selected': { color: DEEP_TEAL },
            '& .MuiTabs-indicator': { bgcolor: DEEP_TEAL },
          }}
        >
          <Tab
            value="emails"
            icon={<EmailTabIcon sx={{ fontSize: 16 }} />}
            iconPosition="start"
            label="Emails"
          />
          <Tab
            value="funnels"
            icon={<FunnelTabIcon sx={{ fontSize: 16 }} />}
            iconPosition="start"
            label="Email Funnels"
          />
        </Tabs>
      </Box>

      {/* Error */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ my: 1 }}>
          {error}
        </Alert>
      )}

      {/* ── Emails Tab ─────────────────────────────────────────────────── */}
      {tab === 'emails' && (
        <Box sx={{ display: 'flex', flex: 1, minHeight: 0, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', bgcolor: 'background.paper', mt: 0 }}>
          {/* Column 1: Channel Sidebar */}
          <ChannelSidebar
            accounts={accounts}
            phoneNumbers={phoneNumbers}
            selectedAccountId={selectedAccountId}
            onSelectAccount={(id) => {
              setSelectedAccountId(id);
              setSelectedItem(null);
            }}
            onManageAccounts={() => setDrawerOpen(true)}
            totalUnread={stats?.unreadCount || 0}
            folder={folder}
            onFolderChange={handleFolderChange}
            draftCount={drafts.length}
          />

          {/* Column 2: Message List */}
          <MessageList
            items={allItems}
            loading={loading}
            selectedItem={selectedItem}
            onSelectItem={folder === 'drafts'
              ? (item) => {
                  const draft = drafts.find(d => d.id === item.id);
                  if (draft) { setEditDraft(draft); setComposeOpen(true); }
                }
              : handleSelectItem
            }
            filter={filter}
            onFilterChange={setFilter}
            search={search}
            onSearchChange={setSearch}
            onSearchSubmit={fetchInbox}
            onRefresh={handleRefresh}
            stats={stats}
            accounts={accounts}
            isEmailConfigured={isEmailConfigured}
            onConnectEmail={() => setDrawerOpen(true)}
          />

          {/* Column 3: Message Detail */}
          {folder !== 'drafts' && (
            <MessageDetail
              selectedItem={selectedItem}
              accounts={accounts}
              onReply={handleReply}
              onResolve={handleResolve}
              onUnresolve={handleUnresolve}
              onCallback={handleCallback}
              onMarkRead={() => selectedItem && handleSelectItem(selectedItem)}
              onTagsUpdated={handleTagsUpdated}
            />
          )}
        </Box>
      )}

      {/* ── Email Funnels Tab ──────────────────────────────────────────── */}
      {tab === 'funnels' && (
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', pt: 2 }}>
          <FunnelBoard
            onOpenEmail={(externalId) => {
              setTab('emails');
              setFolder('all');
              // The inbox will refresh; user can find the email there
            }}
          />
        </Box>
      )}

      {/* Account Manager Drawer */}
      <AccountManagerDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onAccountsChanged={handleAccountsChanged}
      />

      {/* Compose / Edit Draft Dialog */}
      <ComposeDialog
        open={composeOpen}
        onClose={() => { setComposeOpen(false); setEditDraft(null); }}
        accounts={accounts}
        existingDraft={editDraft}
        onSent={() => {
          fetchInbox();
          if (folder === 'sent') fetchInbox();
        }}
        onDraftSaved={() => {
          fetchDrafts();
        }}
      />

      {/* Callback Dialog */}
      <Dialog open={callbackDialogOpen} onClose={() => setCallbackDialogOpen(false)} maxWidth="sm">
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhoneIcon color="primary" />
            Callback
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {callbackResult}
          </Typography>
          {selectedItem?.caller_phone && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <PhoneIcon />
              <Typography variant="h6">{selectedItem.caller_phone}</Typography>
              <Tooltip title="Copy to clipboard">
                <Button
                  size="small"
                  startIcon={<CopyIcon />}
                  onClick={() => navigator.clipboard.writeText(selectedItem.caller_phone!)}
                >
                  Copy
                </Button>
              </Tooltip>
            </Box>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Open Google Voice to dial this number. When Google Voice API is approved, calls will be initiated automatically from this dashboard.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCallbackDialogOpen(false)} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerSupport;
